import { useCallback, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column, SortingRule } from 'react-table';

import { totalProjectCount } from '../../../../../helpers/teams';
import { useTeams } from '../../../../../hooks/api/teams';
import SimpleReactTable from '../../../../generic/SimpleReactTable';
import TableHeader from '../../../../generic/TableHeader';
import type { TeamWithAssociations } from '../../../../../../shared/types/routes/teams';

import TeamsTableRowActions from './RowActions';

const defaultSortMode: SortingRule[] = [{
  id: 'name',
  desc: false,
}];

const useTeamsTableColumns = (sortMode: SortingRule): Array<Column<TeamWithAssociations>> => useMemo(() => [
  {
    id: 'name',
    Header: <TableHeader title="Team Name" sort={{ id: 'name', currentMode: sortMode }} />,
    accessor: 'name',
    resizable: false,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'memberCount',
    Header: <TableHeader title="Members" sort={{ id: 'memberCount', currentMode: sortMode }} />,
    accessor: ({ teamUsers }: TeamWithAssociations) => teamUsers?.length || 0,
    width: 250,
    resizable: false,
  },
  {
    id: 'projectCount',
    Header: <TableHeader title="Projects" sort={{ id: 'projectCount', currentMode: sortMode }} />,
    accessor: (row: TeamWithAssociations) => totalProjectCount(row),
    width: 250,
    resizable: false,
  },
  {
    id: 'actions',
    Header: <TableHeader title="Actions" />,
    accessor: () => null,
    Cell: ({ original }: { original: TeamWithAssociations }) => (
      <TeamsTableRowActions
        teamId={original.id}
        teamName={original.name}
      />
    ),
    width: 100,
    resizable: false,
    sortable: false,
  },
], [sortMode]);

type Props = {
  search: string;
};

/**
 * Filters the teams based on the search term.
 * @param {TeamWithAssociations[]} teams - The list of teams to filter
 * @param {string} search - The search term
 * @returns {TeamWithAssociations[]} The filtered list of teams
 */
const useFilteredTeams = (teams: TeamWithAssociations[], search: string) => useMemo(() => {
  if (!search) {
    return teams;
  }
  const searchLower = search.toLowerCase();
  return teams.filter((team) => team.name.toLowerCase().includes(searchLower));
}, [teams, search]);

/**
 * Displays the list of teams in a table format
 */
const TeamsTable: VoidFunctionComponent<Props> = ({ search }) => {
  const { data: teams, isLoading: isFetchingTeams } = useTeams();
  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode[0] as SortingRule);
  const filteredTeams = useFilteredTeams(teams ?? [], search);
  const onSortedChanged = useCallback((newSortMode: SortingRule) => setSortMode(newSortMode), []);
  const columns = useTeamsTableColumns(sortMode);

  return (
    <SimpleReactTable
      data={filteredTeams}
      columns={columns}
      defaultSortMode={defaultSortMode}
      onSortedChanged={onSortedChanged}
      showPagination={filteredTeams.length > 20}
      cacheId="organizationTeams"
      isLoading={isFetchingTeams}
    />
  );
};

export default TeamsTable;
